import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  InputLabel,
  TextField,
  Button,
  Snackbar,
  Stack,
} from '@mui/material';

import {
  saveResponseMobile,
  saveResponseMobileToken,
  saveResponseEmail,
  saveResponseEmailToken,
  getResponse,
  getResponseModes,
  cancelReturn,
  hideMessage,
} from '../actions/Response';
import { TypingValidateMobile } from '../util/General';
import {
  MOBILE_INPUT_STATE,
  MOBILE_VERIFICATION_STATE,
  EMAIL_INPUT_STATE,
  EMAIL_VERIFICATION_STATE,
  MOBILE_MODE,
  EMAIL_MODE,
} from '../constants/Constants'
import useDocumentTitle from './useDocumentTitle';

const ReturningSession = ({ formId, sessionId, sessionIdFromParam }) => {
  const dispatch = useDispatch();
  const { formData, loading } = useSelector((state) => state.form);
  const {
    alertMessage,
    showMessage,
    mobileSaved,
    mobileVerified,
    mobileTokenExpired,
    emailSaved,
    emailVerified,
    emailTokenExpired,
    modes,
  } = useSelector((state) => state.response);
  const [openMessage, setOpenMessage] = useState(false);
  const [saveState, setSaveState] = useState(MOBILE_INPUT_STATE); // States: MOBILE_INPUT_STATE, MOBILE_VERIFICATION_STATE, EMAIL_INPUT_STATE, EMAIL_VERIFICATION_STATE
  const [mobile, setMobile] = useState('');
  const [isMobileValid, setIsMobileValid] = useState();
  const [mobileToken, setMobileToken] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState();
  const [emailToken, setEmailToken] = useState('');
  const [returningSession, setReturningSession] = useState(false);

  const client_id = formId?.split(":")?.[0];
  const form_id = formId?.split(":")?.[1];
  const buttonStyle = {
    width: "20%"
  };

  useDocumentTitle(formData?.form_title ?? 'Online Application')

  useEffect(() => {
    if (showMessage) {
      setOpenMessage(true);
    }
  }, [showMessage, alertMessage]);

  useEffect(() => {
    if (mobileSaved) {
      setSaveState(MOBILE_VERIFICATION_STATE)
    }
  }, [mobileSaved]);

  useEffect(() => {
    if (mobileVerified) {
      // retrieve response now
      dispatch(getResponse(`${form_id}:${sessionId}`))
    }
  }, [mobileVerified]);

  useEffect(() => {
    if (emailSaved) {
      setSaveState(EMAIL_VERIFICATION_STATE)
    }
  }, [emailSaved]);

  useEffect(() => {
    if (emailVerified) {
      // retrieve response now
      dispatch(getResponse(`${form_id}:${sessionId}`))
    }
  }, [emailVerified]);

  const handleCloseMessage = () => {
    setOpenMessage(false);
    dispatch(hideMessage());
  };

  const handleMobile = (event) => {
    let initialValue = event.target.value;
    if (initialValue.match(/[^0-9+\s]/gi)) {
      return;
    }
    const data = TypingValidateMobile(initialValue);
    setMobile(data.intl_number ? data.intl_number : data.org_number);
    setIsMobileValid(data.is_valid);
  };

  const handleSaveMobile = () => {
    if (isMobileValid && client_id && form_id) {
      const responseData = {
        id: `${form_id}:${sessionId}`,
        mobile,
        client_id,
        form_id,
        session_id: sessionId
      };
      dispatch(saveResponseMobile(responseData))
    }
  }

  const handleMobileToken = (event) => {
    let initialValue = event.target.value
    if (initialValue.match(/[^0-9]/gi) || initialValue.length > 6) {
      return;
    }
    setMobileToken(event.target.value);
  };

  const handleSaveMobileToken = () => {
    if (mobileToken && mobileToken.length === 6) {
      const responseData = {
        id: `${form_id}:${sessionId}`,
        token: mobileToken,
        client_id,
        form_id,
        session_id: sessionId
      };
      dispatch(saveResponseMobileToken(responseData))
    }
  };

  const handleGenerateNewToken = () => {
    setMobileToken('');
    handleSaveMobile();
  };

  const handleEmail = (event) => {
    setEmail(event.target.value);
  };

  const handleSaveEmail = () => {
    if (email) {
      const responseData = {
        id: `${form_id}:${sessionId}`,
        email,
        client_id,
        form_id,
        session_id: sessionId
      };
      dispatch(saveResponseEmail(responseData))
    }
  };

  const handleEmailToken = (event) => {
    let initialValue = event.target.value
    if (initialValue.match(/[^0-9]/gi) || initialValue.length > 6) {
      return;
    }
    setEmailToken(event.target.value);
  };

  const handleSaveEmailToken = () => {
    if (emailToken && emailToken.length === 6) {
      const responseData = {
        id: `${form_id}:${sessionId}`,
        token: emailToken,
        client_id,
        form_id,
        session_id: sessionId
      };
      dispatch(saveResponseEmailToken(responseData))
    }
  };

  const handleGenerateNewEmailToken = () => {
    setEmailToken('');
    handleSaveEmail();
  };

  const handleCancelReturn = () => {
    dispatch(cancelReturn());
  };

  const currentMode = modes[0];

  return (
    <div style={{ textAlign: 'center' }}>
      <Typography variant={'h6'} gutterBottom>{formData?.autosave?.header ?? ''}</Typography>
      <Typography variant={'subtitle1'} gutterBottom>{formData?.autosave?.intro_text ?? ''}</Typography>

      { currentMode === MOBILE_MODE && saveState !== MOBILE_VERIFICATION_STATE &&
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <InputLabel style={{ marginTop: '3em' }}>Please provide your mobile number:</InputLabel>
          <TextField
            style={{ width: '80%' }}
            name="mobile"
            value={mobile}
            onChange={handleMobile}
            inputProps={{
              style: { textAlign: 'center' }
            }}
          />
          <Button
            style={buttonStyle}
            color='primary'
            variant='contained'
            onClick={handleSaveMobile}
          >
            Next
          </Button>
        </Stack>
      }
      { saveState === MOBILE_VERIFICATION_STATE &&
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <InputLabel style={{ marginTop: '3em' }}>We just sent you a code, please enter below:</InputLabel>
          <TextField
            style={{ width: '80%' }}
            name="mobileToken"
            value={mobileToken}
            onChange={handleMobileToken}
            inputProps={{
              style: { textAlign: 'center' }
            }}
          />
          <Button
            style={buttonStyle}
            color='primary'
            variant='contained'
            onClick={handleSaveMobileToken}
          >
            Resume
          </Button>
          {mobileTokenExpired &&
            <Button
              color='primary'
              variant='text'
              size='small'
              onClick={handleGenerateNewToken}
            >
              Generate another token
            </Button>
          }
        </Stack>
      }
      { currentMode === EMAIL_MODE && saveState !== EMAIL_VERIFICATION_STATE &&
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <InputLabel style={{ marginTop: '3em' }}>Please provide your email address:</InputLabel>
          <TextField
            style={{ width: '80%' }}
            required
            name="email"
            value={email}
            onChange={handleEmail}
            inputProps={{
              style: { textAlign: 'center' }
            }}
            type="email"
          />
          <Button
            style={buttonStyle}
            color='primary'
            variant='contained'
            onClick={handleSaveEmail}
          >
            Next
          </Button>
        </Stack>
      }
      { saveState === EMAIL_VERIFICATION_STATE &&
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <InputLabel style={{ marginTop: '3em' }}>We just sent you a code, please enter below:</InputLabel>
          <TextField
            style={{ width: '80%' }}
            name="emailToken"
            value={emailToken}
            onChange={handleEmailToken}
            inputProps={{
              style: { textAlign: 'center' }
            }}
          />
          <Button
            style={buttonStyle}
            color='primary'
            variant='contained'
            onClick={handleSaveEmailToken}
          >
            Next
          </Button>
          {emailTokenExpired &&
            <Button
              color='primary'
              variant='text'
              size='small'
              onClick={handleGenerateNewEmailToken}
            >
              Generate another token
            </Button>
          }
        </Stack>
      }
      <Button
        style={{ marginTop: '3em' }}
        color='secondary'
        variant='text'
        size='small'
        onClick={handleCancelReturn}
      >
        I want to start a new application
      </Button>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openMessage}
        autoHideDuration={5000}
        onClose={handleCloseMessage}
        message={alertMessage}
      />
    </div>
  );
};

export default ReturningSession;
